/**
 * Consume text/event-stream (SSE) with Authorization Bearer.
 * Browser EventSource cannot set headers - required when API expects Supabase JWT.
 */
import { resolveUrl } from '@/lib/api/core'
import { supabase } from '@/lib/supabase'

export type AgentSseCallbacks = {
  onStatusJSON?: (data: Record<string, unknown>) => void
  /** HTTP 401 or missing session before fetch */
  onAuthRequired?: (detail: { message: string; status?: number }) => void
  onHttpError?: (detail: { status: number; message: string }) => void
  onDone?: () => void
}

export function buildAgentStreamUrl(agentId: string, organizationId?: string | null): string {
  const base = `/api/cursor-cloud/agents/${encodeURIComponent(agentId)}/stream`
  if (organizationId && String(organizationId).trim()) {
    return `${base}?organizationId=${encodeURIComponent(String(organizationId).trim())}`
  }
  return base
}

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string; message?: string }
    return data.error || data.message || res.statusText || `HTTP ${res.status}`
  } catch {
    return res.statusText || `HTTP ${res.status}`
  }
}

function parseBlocks(buffer: string): { events: Array<{ event: string; data: string }>; rest: string } {
  const events: Array<{ event: string; data: string }> = []
  const sep = '\n\n'
  let rest = buffer
  for (;;) {
    const idx = rest.indexOf(sep)
    if (idx === -1) break
    const raw = rest.slice(0, idx).replace(/\r\n/g, '\n')
    rest = rest.slice(idx + sep.length)
    if (!raw.trim().length || raw.startsWith(':')) continue
    let eventName = 'message'
    const dataLines: string[] = []
    for (const line of raw.split('\n')) {
      if (!line.length) continue
      if (line.startsWith(':')) continue
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim() || 'message'
        continue
      }
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).startsWith(' ') ? line.slice(6) : line.slice(5))
      }
    }
    if (!dataLines.length) continue
    events.push({ event: eventName, data: dataLines.join('\n') })
  }
  return { events, rest }
}

/**
 * Opens an authenticated fetch stream to URL (relative paths use API_BASE_URL).
 */
export async function subscribeAgentSseStream(
  streamUrl: string,
  cbs: AgentSseCallbacks,
  signal?: AbortSignal
): Promise<void> {
  const url =
    streamUrl.startsWith('http://') || streamUrl.startsWith('https://')
      ? streamUrl
      : resolveUrl(streamUrl)

  const headers = new Headers()
  let hasToken = false
  if (supabase) {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (session?.access_token) {
      headers.set('Authorization', `Bearer ${session.access_token}`)
      hasToken = true
    }
  }

  if (!hasToken) {
    cbs.onAuthRequired?.({
      message: 'You are not signed in. Sign in to Maater, then try Watch live again.',
    })
    cbs.onDone?.()
    return
  }

  try {
    const res = await fetch(url, { headers, signal })

    if (res.status === 401 || res.status === 403) {
      const msg = await readErrorMessage(res)
      cbs.onAuthRequired?.({
        status: res.status,
        message:
          msg === 'Authentication required'
            ? 'Your session expired or is invalid. Refresh the page and sign in again, then reopen Watch live.'
            : msg,
      })
      cbs.onDone?.()
      return
    }

    if (!res.ok || !res.body) {
      const msg = await readErrorMessage(res)
      cbs.onHttpError?.({ status: res.status, message: msg })
      cbs.onStatusJSON?.({ text: `[ERROR] Agent stream HTTP ${res.status}: ${msg}` })
      cbs.onDone?.()
      return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const { events, rest } = parseBlocks(buffer)
        buffer = rest
        for (const ev of events) {
          if (ev.event === 'done') {
            cbs.onDone?.()
            continue
          }
          if (ev.event === 'error') {
            try {
              const data = JSON.parse(ev.data) as { message?: string }
              cbs.onStatusJSON?.({ text: `[ERROR] ${data.message || 'Stream error'}` })
            } catch {
              cbs.onStatusJSON?.({ text: `[ERROR] ${ev.data}` })
            }
            continue
          }
          if (ev.event !== 'status') continue
          try {
            const data = JSON.parse(ev.data) as Record<string, unknown>
            cbs.onStatusJSON?.(data)
          } catch {
            // malformed chunk - ignore
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
    cbs.onDone?.()
  } catch (err) {
    if (signal?.aborted) return
    const message = err instanceof Error ? err.message : 'Connection dropped'
    cbs.onHttpError?.({ status: 0, message })
    cbs.onStatusJSON?.({ text: `[ERROR] Agent stream connection dropped: ${message}` })
    cbs.onDone?.()
  }
}
